import { env } from '@tencent/vft-common';
import { getUrlParam } from '@tencent/vft-common/util';
import UniversalReport from '@tencent/universal-report';
import iwanLogin from '@tencent/iwan-login';

import { getPlatform, getPvid, getReportType, query2obj } from '../util';
import JsApi from '../jsApi';

const BOSS_DT_KEYMAP = {
  f_magic_modid: 'mod_id',
  f_module: 'mod_title',
  f_resource: 'btn_title',
};

const STANDARD_EVENTS = {
  PGIN: 'dt_pgin',
  PGOUT: 'dt_pgout',
  IMP: 'dt_imp',
  IMPEND: 'dt_imp_end',
  CLCK: 'dt_clck',
};

export default class UniversalReporter {
  constructor(options) {
    this.trackCallbacks = [];
    let hardwareOs = env.os;
    if (env.isPCClient) {
      hardwareOs = 'windows';
    } else if (env.isMac) {
      hardwareOs = 'macos';
    }

    const { pageId, channel, monitoringJsApiReport, extraDefaultChannel } =
      options;

    this.$reporter = new UniversalReport({
      beacon: '0WEB05G2MB05VBOY',
      formatMode: 1,
      channel,
      monitoringJsApiReport,
      extraDefaultChannel,
      contentidSet: {
        [pageId]: `${pageId}_init`,
      },
      publicParams: {
        // 基础公共参数覆盖
        platform: env.isWeb ? 'pc' : 'phone',
        hardware_os: hardwareOs,
        // 自定义公共参数
        ptag: getUrlParam('ptag'),
        aid: getUrlParam('aid'),
        rsc_id: `ResourceID=${getUrlParam('ResourceID')}&SourceID=${getUrlParam(
          'SourceID'
        )}&via2=${getUrlParam('via2')}`,
        url: location.href,
        ref_url: document.referrer,
        url_param: location.search.substr(1),
        domain: location.hostname,
        pt: getPlatform(),
        screen_height: window.screen.height,
        screen_width: window.screen.width,
        screen_size_class: getScreenSize(),
        hh_ua_raw: navigator.userAgent,
      },
    });
    this.options = options;
    JsApi.on('onActionLoginFinish', this.setPublicParams.bind(this));
    JsApi.on('onActionLogoutFinish', this.setPublicParams.bind(this));
    JsApi.on('onSwitchLoginStateResponse', this.setPublicParams.bind(this));

    this.$magicReport = this.options?.vm?.$magicReport;
  }

  async ready() {
    const { pageId, pageRoot } = this.options;
    if (pageId) {
      this.emitTrack('setPublicParams.begin');
      await this.setPublicParams();
      this.emitTrack('setPublicParams.end');
      this.$reporter
        .setPage(
          {
            pgid: pageId,
            contentid: `${pageId}_init`,
          },
          pageRoot || document.body
        )
        .observe(pageId);
    } else {
      console.error('[UN Report] 请设置你在大同配置的页面id');
    }
  }

  report(params, type = 'expose') {
    if (!params) {
      throw new Error('[UN Report] 参数不合法');
    }
    // 魔方历史代码的boss上报转大同上报，后加入大同自定义上报事件的处理
    // 如果是旧的boss上报，有click，expose，module，都转成大同的内置事件点击和曝光，其余的认为是自定义事件，eventName=type
    let eventName = type;
    if (['expose', 'module'].includes(type)) {
      eventName = STANDARD_EVENTS.IMP;
    } else if (type === 'click') {
      eventName = STANDARD_EVENTS.CLCK;
    }
    const businessParams = this.boss2datong(params);
    this.$reporter.reportEvent({
      pgid: this.options.pageId,
      eventName,
      publicParams: {
        dt_eid: params.eid || (params.f_type === 4 ? 'button' : 'module'),
      },
      businessParams,
    });
  }

  useDirective(el, binding) {
    if (typeof el?.setAttribute !== 'function') {
      return;
    }
    const type = getReportType(binding.modifiers);
    const params = binding.value;
    if (type === 'pv' || !params) {
      return;
    }
    if (type === 'module' && params._module) {
      params.mod_title = params._module;
      params.btn_title = params._module;
    }
    const preDtParams = el.getAttribute('dt-params');
    const newDtParamsObj = {
      ...query2obj(preDtParams),
      ...params,
    };
    el.setAttribute('dt-params', obj2query(newDtParamsObj));
    let eid = params.eid || el.getAttribute('dt-eid');
    if (!eid) {
      eid = params.f_type === 4 ? 'button' : 'module';
      console.warn(
        `[UN Report] 上传参数 ${JSON.stringify(
          params
        )} 中 'eid' 缺失，使用默认的eid: ${eid}`
      );
    }
    el.setAttribute('dt-eid', eid);
  }

  setExtraParams(params = {}) {
    this.presetParams = {
      ...this.presetParams,
      ...params,
    };
  }

  async setPublicParams() {
    const extraParams = this.options?.extraParams
      ? await this.options.extraParams()
      : {};
    this.emitTrack('getBaseParamsAndGetLoginInfo.begin');
    const [baseParams, { result }] = await Promise.all([
      this.getBaseParams(),
      iwanLogin.getLoginInfo(),
    ]);
    this.emitTrack('getBaseParamsAndGetLoginInfo.end');
    const loginInfo = result || {};
    this.$reporter.setPublicParams({
      hc_pvid: getPvid(),
      appid: loginInfo.appid || '',
      qq_openid: loginInfo.type === 'qq' ? loginInfo.openid : '',
      wx_openid: loginInfo.type === 'wx' ? loginInfo.openid : '',
      qq: '', // iwanLogin取不到uin
      vuserid: loginInfo.vuserid,
      main_login: loginInfo.type === 'nologin' ? '' : loginInfo.type,
      ...baseParams,
      ...extraParams,
    });

    this.emitTrack('iwanLogin.isLogin.begin');
    const { result: isLogin } = await iwanLogin.isLogin();
    this.emitTrack('iwanLogin.isLogin.end');
    if (isLogin) {
      const info = {
        dt_mainlogin_h5: loginInfo.type,
        dt_qq_h5: '', // iwanLogin取不到uin
      };
      info[`dt_${loginInfo.type}openid_h5`] = loginInfo.openid;
      this.$reporter.setAccountInfo(info);
    } else {
      this.$reporter.clearAccountInfo();
    }
  }

  async getBaseParams() {
    this.emitTrack('getBaseParams.begin');
    if (this.baseParams) {
      this.emitTrack('getBaseParams.end');
      return Promise.resolve(this.baseParams);
    }
    let deviceInfo = {};
    let appInfo = {};

    // 动漫没有实现 getDeviceInfo 和 getAppInfo
    if (!JsApi?.env?.isAc) {
      try {
        deviceInfo = (await JsApi.invoke('getDeviceInfo'))?.result || {};
        if (JsApi?.env?.app !== 'yyb') {
          appInfo = (await JsApi.invoke('getAppInfo'))?.result || {};
        }
      } catch (e) {
        console.error(e);
      }
    }

    this.baseParams = {
      pgid: '',
      app_vr: appInfo.version || '',
      os_vrsn: deviceInfo.systemVersion || '',
      guid: deviceInfo.guid || '',
      omgid: deviceInfo.omgid || '',
      jlbrk: deviceInfo.isJBOS || '0',
    };
    this.emitTrack('getBaseParams.end');
    return this.baseParams;
  }

  boss2datong(params = {}) {
    const result = {};
    Object.keys(params).forEach((key) => {
      if (BOSS_DT_KEYMAP[key]) {
        result[BOSS_DT_KEYMAP[key]] = params[key];
      } else {
        result[key] = params[key];
      }
    });
    return result;
  }

  onTrack(callback) {
    this.trackCallbacks.push(callback);
  }

  emitTrack(eventName) {
    this.trackCallbacks.forEach((callback) => {
      try {
        callback(eventName);
      } catch (e) {
        // ignore，用于定位上报耗时，不需要处理
      }
    });
  }
}

function obj2query(params) {
  const queryArr = [];
  const data = params;
  Object.keys(data)
    .filter((key) => key !== 'eid')
    .forEach((key) => {
      const newKey = BOSS_DT_KEYMAP[key] || key;
      queryArr.push(`${newKey}=${data[key]}`);
    });
  return queryArr.join('&');
}

function getScreenSize() {
  const size = {
    regular: 375,
    large: 667,
    huge: 980,
    max: 1366,
  };
  const screenWidth = document.documentElement.clientWidth;
  let dpr = window.devicePixelRatio;
  let dpi = 1;
  if (dpr > 1 && dpr < 3) {
    dpi = 2;
  } else if (dpr >= 3) {
    dpi = 3;
  } else {
    dpi = 1;
  }
  /*
   * 2020.5.12
   * 普通屏幕是160ppi，也就是我们常说的1倍屏幕。
   * 如果是320ppi，也就是我们常说的2倍屏幕。
   * 现在所有的设备都是160ppi+，高清的iphone XM 就是458ppi，虽然dpr是3，但其实是不够3倍屏的，所以还是以2倍屏来处理。
   * 2020.5.26
   * 有某些特殊的安卓机型dpr居然是4，所以有了以下判断，
   */
  if (dpr >= 4 && env.isAndroid) {
    dpr = 2;
  }
  dpi = 2;
  let index =
    Object.values(size).findIndex((el) => el > (dpr * screenWidth) / dpi) - 1;
  if (index === -1) {
    index = 0;
  } else if (index === -2) {
    index = Object.values(size).length - 1;
  }
  return Object.keys(size)[index];
}
